// Connection: WebSocket to the portal + E2E handshake + the message protocol
// the daemon speaks (rpc / rpc.res / event / vapid / push.sub).
import { b64decode, b64encode, finish, generateKeypair, initiate, Session } from "./crypto.js";
import type { Msg2Payload } from "./crypto.js";
import type { DaemonEntry } from "./store.js";

const RPC_TIMEOUT_MS = 20_000;
const HANDSHAKE_TIMEOUT_MS = 15_000;

export interface RPCResponse {
  status: number;
  body: unknown;
}

export interface ConnectionHandlers {
  onEvent?: (type: string, payload: unknown) => void;
  onVapid?: (publicKey: string) => void;
  onClose?: () => void;
}

interface ServerMsg {
  kind: string;
  id?: string;
  status?: number;
  body?: unknown;
  type?: string;
  payload?: unknown;
  publicKey?: string;
}

export interface PairingCredentials {
  secret: string; // base64, from the QR
  deviceName: string;
}

export class Connection {
  private ws?: WebSocket;
  private sess?: Session;
  private pending = new Map<string, { resolve: (r: RPCResponse) => void; reject: (e: Error) => void; timer: number }>();
  private nextId = 1;
  private closedByUs = false;
  vapidKey = "";

  constructor(
    private entry: DaemonEntry,
    private handlers: ConnectionHandlers = {},
    private pairing?: PairingCredentials,
  ) {}

  // connect dials the portal and runs the E2E handshake. Resolves with the
  // daemon's authenticated hello.
  connect(): Promise<Msg2Payload> {
    const wsBase = this.entry.portal.replace(/^http/, "ws");
    const ws = new WebSocket(`${wsBase}/v1/connect/${this.entry.daemonId}`);
    ws.binaryType = "arraybuffer";
    this.ws = ws;

    return new Promise<Msg2Payload>((resolve, reject) => {
      const fail = (msg: string) => {
        clearTimeout(timer);
        reject(new Error(msg));
        ws.close();
      };
      const timer = window.setTimeout(() => fail("handshake timed out — is the daemon online?"), HANDSHAKE_TIMEOUT_MS);

      ws.onerror = () => fail("could not reach the relay (daemon offline?)");
      ws.onclose = () => {
        if (!this.closedByUs) this.handlers.onClose?.();
      };
      ws.onopen = () => {
        try {
          const phoneStatic = generateKeypair(b64decode(this.entry.myPriv));
          const payload: Record<string, unknown> = { v: 1 };
          if (this.pairing) {
            payload.secret = this.pairing.secret;
            payload.name = this.pairing.deviceName;
          }
          const { msg1, state } = initiate(
            this.entry.daemonId,
            b64decode(this.entry.daemonPub),
            phoneStatic,
            new TextEncoder().encode(JSON.stringify(payload)),
          );
          ws.send(msg1);
          ws.onmessage = (e: MessageEvent) => {
            try {
              const { session, payload: hello } = finish(state, new Uint8Array(e.data as ArrayBuffer));
              this.sess = session;
              clearTimeout(timer);
              ws.onmessage = (ev: MessageEvent) => this.onFrame(new Uint8Array(ev.data as ArrayBuffer));
              resolve(hello);
            } catch {
              fail("handshake rejected — re-pair this daemon from Fabrika settings");
            }
          };
        } catch (err) {
          fail((err as Error).message);
        }
      };
    });
  }

  private onFrame(frame: Uint8Array): void {
    if (!this.sess) return;
    let msg: ServerMsg;
    try {
      msg = JSON.parse(new TextDecoder().decode(this.sess.open(frame))) as ServerMsg;
    } catch {
      this.close();
      return;
    }
    switch (msg.kind) {
      case "rpc.res": {
        const waiter = this.pending.get(msg.id ?? "");
        if (waiter) {
          this.pending.delete(msg.id ?? "");
          clearTimeout(waiter.timer);
          waiter.resolve({ status: msg.status ?? 0, body: msg.body });
        }
        break;
      }
      case "event":
        this.handlers.onEvent?.(msg.type ?? "", msg.payload);
        break;
      case "vapid":
        this.vapidKey = msg.publicKey ?? "";
        this.handlers.onVapid?.(this.vapidKey);
        break;
    }
  }

  private sendMsg(v: Record<string, unknown>): void {
    if (!this.sess || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error("not connected");
    }
    this.ws.send(this.sess.seal(new TextEncoder().encode(JSON.stringify(v))));
  }

  // request relays an API call through the daemon's allowlisted bridge.
  request(method: string, path: string, body?: unknown): Promise<RPCResponse> {
    const id = `r${this.nextId++}`;
    return new Promise<RPCResponse>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`${method} ${path}: timed out`));
      }, RPC_TIMEOUT_MS);
      this.pending.set(id, { resolve, reject, timer });
      try {
        this.sendMsg({ kind: "rpc", id, method, path, ...(body !== undefined ? { body } : {}) });
      } catch (e) {
        this.pending.delete(id);
        clearTimeout(timer);
        reject(e as Error);
      }
    });
  }

  // api unwraps a request, throwing on non-2xx like a fetch wrapper would.
  async api<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await this.request(method, path, body);
    if (res.status < 200 || res.status >= 300) {
      const err = (res.body as { error?: string } | undefined)?.error;
      throw new Error(err ?? `${method} ${path}: ${res.status}`);
    }
    return res.body as T;
  }

  registerPush(sub: PushSubscriptionJSON): void {
    this.sendMsg({ kind: "push.sub", sub });
  }

  unregisterPush(endpoint: string): void {
    this.sendMsg({ kind: "push.unsub", endpoint });
  }

  close(): void {
    this.closedByUs = true;
    for (const [, waiter] of this.pending) {
      clearTimeout(waiter.timer);
      waiter.reject(new Error("connection closed"));
    }
    this.pending.clear();
    this.ws?.close();
  }
}

// daemonStatus is the cheap unencrypted pre-flight: is the daemon's tunnel up?
export async function daemonStatus(entry: DaemonEntry): Promise<{ online: boolean; sessions: number }> {
  try {
    const res = await fetch(`${entry.portal}/v1/status/${entry.daemonId}`);
    if (!res.ok) return { online: false, sessions: 0 };
    return (await res.json()) as { online: boolean; sessions: number };
  } catch {
    return { online: false, sessions: 0 };
  }
}

export { b64encode };
